import { type Duration, type Timestamp, timestampDate, timestampFromMs, timestampMs } from '@bufbuild/protobuf/wkt';
import { formatMillisecondsAsHms } from '@qdrant-cloud-ui/date/format-milliseconds-as-hms';
import { getUTCOffset } from '@qdrant-cloud-ui/date/get-utc-offset';
import { millisecondsInDay } from '@qdrant-cloud-ui/date/milliseconds-in-day';
import { millisecondsToDays } from '@qdrant-cloud-ui/date/milliseconds-to-days';
import { toUtcWallClock } from '@qdrant-cloud-ui/date/to-utc-wall-clock';
import { FORMAT_LOCALE } from './constants';

const DATE_FORMAT_CONFIG = Object.freeze({
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});
const LONG_DATE_FORMAT_CONFIG: Intl.DateTimeFormatOptions = Object.freeze({
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const TIME_FORMAT_CONFIG = Object.freeze({
  hour: 'numeric',
  minute: 'numeric',
  hour12: true,
});
const DATE_TIME_FORMAT_CONFIG = Object.freeze({
  day: 'numeric',
  hour: 'numeric',
  hour12: true,
  minute: 'numeric',
  month: 'short',
  year: 'numeric',
});

// Example: "Mar 10, 2025"
const dateFormatter = new Intl.DateTimeFormat(FORMAT_LOCALE, DATE_FORMAT_CONFIG);
// Example: "March 10, 2025"
const longDateFormatter = new Intl.DateTimeFormat(FORMAT_LOCALE, LONG_DATE_FORMAT_CONFIG);
// Example: "3:30 PM"
const timeFormatter = new Intl.DateTimeFormat(FORMAT_LOCALE, TIME_FORMAT_CONFIG);
// Example: "Mar 10, 2025, 3:30 PM"
const dateTimeFormatter = new Intl.DateTimeFormat(FORMAT_LOCALE, DATE_TIME_FORMAT_CONFIG);

const formatWithOffset = ({
  date,
  utc = false,
  formatter,
}: {
  date: Date;
  utc?: boolean;
  formatter: Intl.DateTimeFormat;
}) => {
  if (utc) {
    const formattedDate = formatter.format(toUtcWallClock(date));
    return `${formattedDate} (UTC)`;
  }

  const formattedDate = formatter.format(date);
  const utcOffset = getUTCOffset(date);

  // Construct the final formatted string
  return `${formattedDate} (${utcOffset})`;
};

/**
 * Formats the given date and time to the format 'MMM dd, yyyy h:mm a (UTC±hh:mm)'.
 * If utc flag is set to true, the date and time are formatted in UTC timezone 'MMM dd, yyyy h:mm a (UTC)'.
 */
export const formatDateTimeWithOffset = ({ date, utc = false }: { date: Date; utc?: boolean }) =>
  formatWithOffset({ date, utc, formatter: dateTimeFormatter });

/**
 * Formats the given date to the format 'h:mm a (UTC±hh:mm)'.
 * If utc flag is set to true, time is formatted in UTC timezone 'h:mm a (UTC)'.
 */
export const formatTimeWithOffset = ({ date, utc = false }: { date: Date; utc?: boolean }) =>
  formatWithOffset({ date, utc, formatter: timeFormatter });

export const formatDate = (date: Date) => dateFormatter.format(date);

export const formatLongDate = (date: Date) => longDateFormatter.format(date);

export const formatDateCustom = (date: Date, formatOptions: Intl.DateTimeFormatOptions, locale = FORMAT_LOCALE) =>
  new Intl.DateTimeFormat(locale, formatOptions).format(date);

export const formatUtcDate = (date: Date, withYear = true) =>
  formatDateCustom(date, {
    day: 'numeric',
    month: 'short',
    year: withYear ? 'numeric' : undefined,
    timeZone: 'UTC',
  });

export function timestampToDate(timestamp: Timestamp): Date;
export function timestampToDate(timestamp: undefined): undefined;
export function timestampToDate(timestamp: Timestamp | undefined): Date | undefined;
export function timestampToDate(timestamp: Timestamp | undefined) {
  return timestamp ? timestampDate(timestamp) : undefined;
}

export const getMsTimestampFromUnixTimestamp = (unixTimestamp: number) => unixTimestamp * 1000;
export const getMsTimestampFromNanoTimestamp = (nanoTimestamp: number) => nanoTimestamp / 1e6;
/**
 * Converts a protobuf Duration to milliseconds.
 *
 * WARNING: This function performs a potentially lossy cast from BigInt to Number.
 *
 * timestamp.seconds is a 64-bit integer (int64 in protobuf), so we keep all
 * arithmetic in BigInt instead of Number (which has decreased precision
 * for integers above 2^53). However, the return value is cast to Number
 * for compatibility with JavaScript Date and other APIs.
 *
 * JS Numbers use IEEE-754 double precision and can exactly represent every
 * integer up to Number.MAX_SAFE_INTEGER, i.e. approximately 9.007 × 10^15 ms [1].
 *
 *   9.007 × 10^15 ms  ≈ 285 426 years
 *   1970 (Epoch) + 285 426 years ≈ 287 396 CE
 *
 * So millisecond timestamps remain precise until roughly the year 287 396 CE.
 * (The built-in Date object itself caps out a bit earlier at around 8.64 × 10^15 ms [2],
 * or about the year 275 760 CE—but either limit is far beyond any realistic application.)
 *
 * [1] https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/MAX_SAFE_INTEGER
 * [2] https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#the_epoch_timestamps_and_invalid_date
 * @param duration - The protobuf Duration object
 * @returns The time in milliseconds as a Number (potentially lossy for very large values)
 */
const protoDurationToMilliseconds = (duration: Duration) =>
  timestampMs({
    $typeName: 'google.protobuf.Timestamp',
    seconds: duration.seconds,
    nanos: duration.nanos,
  });

/**
 * Formats a protobuf Duration into a human-readable time string
 * @param duration The protobuf Duration object to format
 * @returns Formatted duration string in "XhYmZs" format. Nanoseconds are ignored.
 */
export function formatProtoDuration(duration: Duration) {
  return formatMillisecondsAsHms(protoDurationToMilliseconds(duration));
}

/**
 * Creates a protobuf Duration from a number of days
 * @param days The number of days
 * @returns A protobuf Duration object
 */
export function daysToDuration(days: number): Duration {
  const ts = timestampFromMs(days * millisecondsInDay);
  return {
    $typeName: 'google.protobuf.Duration',
    seconds: ts.seconds,
    nanos: ts.nanos,
  };
}

/**
 * Converts a protobuf Duration to a number of days
 * @param duration The protobuf Duration object
 * @returns The number of days
 */
export function durationToDays(duration: Duration) {
  return millisecondsToDays(protoDurationToMilliseconds(duration));
}

/** Returns the earlier of two protobuf timestamps, treating `undefined` as "no date" (the other wins). */
export function earliestTimestamp(a: Timestamp | undefined, b: Timestamp | undefined): Timestamp | undefined {
  if (!a) {
    return b;
  }
  if (!b) {
    return a;
  }
  return timestampDate(a) <= timestampDate(b) ? a : b;
}

/** Returns the later of two protobuf timestamps, treating `undefined` as "no date" (the other wins). */
export function latestTimestamp(a: Timestamp | undefined, b: Timestamp | undefined): Timestamp | undefined {
  if (!a) {
    return b;
  }
  if (!b) {
    return a;
  }
  return timestampDate(a) >= timestampDate(b) ? a : b;
}
